import { provideHttpClient } from "@angular/common/http";
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideBrowserGlobalErrorListeners } from "@angular/core";
import { TimeEntryDateAdapter } from "./_helpers/time-entry-date-adapter";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from "@angular/router";
import { provideFuse } from "@fuse";
import { provideTransloco, TranslocoService } from "@ngneat/transloco";
import { firstValueFrom } from "rxjs";
import { appRoutes } from "app/app.routes";
import { provideIcons } from "app/core/icons/icons.provider";
import { TranslocoHttpLoader } from "./core/transloco/transloco.http-loader";
import { provideHttpDefault } from "./core/net/default.provider";
import { QuillModule } from "ngx-quill";
import { ModalModule } from "ngx-bootstrap/modal";
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from "@angular/material/checkbox";
import { DatePipe } from "@angular/common";
import { MAT_TIMEPICKER_CONFIG } from "@angular/material/timepicker";
import { TelemetryErrorHandler } from "./core/telemetry/telemetry-error.handler";

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(),
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withInMemoryScrolling({ scrollPositionRestoration: "enabled" })),
    importProvidersFrom(QuillModule.forRoot(), ModalModule.forRoot()),
    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: TimeEntryDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "D",
          timeInput: "H:mm",
        },
        display: {
          dateInput: "DDD",
          monthYearLabel: "LLL yyyy",
          dateA11yLabel: "DD",
          monthYearA11yLabel: "LLLL yyyy",
          timeInput: "HH:mm a",
          timeOptionLabel: "HH:mm a",
        },
      },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: "en",
            label: "English",
          },
          {
            id: "es",
            label: "Spanish",
          },
        ],
        defaultLang: "en",
        fallbackLang: "en",
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(defaultLang);

        return () => firstValueFrom(translocoService.load(defaultLang));
      },
      multi: true,
    },

    // Fuse
    provideHttpDefault(),
    provideIcons(),
    provideFuse({
      fuse: {
        layout: "modern",
        scheme: "light",
        screens: {
          sm: "600px",
          md: "960px",
          lg: "1280px",
          xl: "1440px",
        },
        theme: "theme-brand",
        themes: [
          {
            id: "theme-default",
            name: "Default",
          },
          {
            id: "theme-brand",
            name: "Brand",
          },
          {
            id: "theme-teal",
            name: "Teal",
          },
          {
            id: "theme-rose",
            name: "Rose",
          },
          {
            id: "theme-purple",
            name: "Purple",
          },
          {
            id: "theme-amber",
            name: "Amber",
          },
        ],
      },
    }),
    {
      // Use the 'fill' appearance on Angular Material Checkbox by default
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: {
        appearance: "fill",
      },
    },
    DatePipe,
    // Global default interval for time picker controls
    {
      provide: MAT_TIMEPICKER_CONFIG,
      useValue: {
        interval: "15m", // Globally set interval to 1 hour
      },
    },
    {
      provide: ErrorHandler,
      useClass: TelemetryErrorHandler,
    },
    // ErrorHandler only sees errors raised inside NgZone; AG Grid and third-party scripts throw from the root zone.
    provideBrowserGlobalErrorListeners(),
  ],
};
